
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    CalendarDays,
    CheckCircle2,
    ChevronRight,
    CircleAlert,
    Clock3,
    CreditCard,
    Edit3,
    Globe2,
    Heart,
    Hotel,
    LockKeyhole,
    Mail,
    MapPin,
    Phone,
    Save,
    ShieldCheck,
    Sparkles,
    Star,
    Trash2,
    UserRound,
    Users,
    X,
    XCircle,
} from "lucide-react";

const API = "https://dubaibudgetivehotels.com/api";

const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-extrabold text-white transition hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-60";

const fieldClass =
    "w-full rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] py-3.5 pl-12 pr-4 text-sm text-[#17324D] outline-none transition placeholder:text-[#9AA8B5] focus:border-[#168B43] focus:bg-white focus:ring-4 focus:ring-[#168B43]/10";

function formatDate(date) {
    if (!date) return "—";
    const d = new Date(date);
    return Number.isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
}

function formatMoney(amount) {
    return Number(amount || 0).toLocaleString("en-AE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function getDeadline(booking) {
    const created = new Date(booking.created_at).getTime();
    const checkIn = new Date(booking.check_in).getTime();

    if (!Number.isFinite(created) || !Number.isFinite(checkIn)) {
        return 0;
    }

    return Math.min(created + 24 * 60 * 60 * 1000, checkIn);
}

function countdown(deadline, now) {
    const seconds = Math.max(0, Math.floor((deadline - now) / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return [hours, minutes, secs]
        .map((n) => String(n).padStart(2, "0"))
        .join(":");
}

function Status({ status }) {
    const value = String(status || "pending").toLowerCase();

    const style =
        value === "confirmed"
            ? "bg-[#E8F7EE] text-[#168B43]"
            : value === "cancelled"
                ? "bg-red-50 text-red-600"
                : "bg-amber-50 text-amber-700";

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[11px] font-extrabold capitalize ${style}`}
        >
            {value === "confirmed" ? (
                <CheckCircle2 size={14} />
            ) : value === "cancelled" ? (
                <XCircle size={14} />
            ) : (
                <Clock3 size={14} />
            )}
            {value}
        </span>
    );
}

function InputField({
    label,
    name,
    value,
    onChange,
    icon: Icon,
    type = "text",
    placeholder,
}) {
    return (
        <div>
            <label
                htmlFor={`profile-${name}`}
                className="mb-2.5 block text-[13px] font-extrabold text-[#17324D]"
            >
                {label}
            </label>

            <div className="relative">
                <Icon
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#168B43]"
                />
                <input
                    id={`profile-${name}`}
                    name={name}
                    type={type}
                    value={value || ""}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={fieldClass}
                />
            </div>
        </div>
    );
}

function DetailTile({ icon: Icon, label, children, accent = false }) {
    return (
        <div
            className={`rounded-2xl border p-4 ${accent
                ? "border-[#B9E4C6] bg-[#EAF8EF]"
                : "border-[#E1ECE5] bg-[#F8FBF9]"
                }`}
        >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#168B43] shadow-sm">
                {Icon ? <Icon size={19} /> : <span className="text-xs font-black">AED</span>}
            </div>

            <p className="mt-4 text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#718096]">
                {label}
            </p>

            <div className="mt-1.5 break-words text-[14px] font-extrabold text-[#17324D]">
                {children}
            </div>
        </div>
    );
}

function RatingStars({ rating, size = 16 }) {
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((number) => (
                <Star
                    key={number}
                    size={size}
                    className={
                        number <= Number(rating)
                            ? "fill-amber-400 text-amber-400"
                            : "text-[#DCEAE1]"
                    }
                />
            ))}
        </div>
    );
}

export default function Profile({ onLoginClick }) {
    const [user, setUser] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        nationality: "",
    });

    const [bookings, setBookings] = useState([]);
    const [activeTab, setActiveTab] = useState("profile");
    const [now, setNow] = useState(Date.now());
    const [profileLoading, setProfileLoading] = useState(false);
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [notice, setNotice] = useState(null);

    const [reviewModal, setReviewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");

    const fetchBookings = useCallback(async (userId) => {
        setBookingsLoading(true);
        try {
            const response = await axios.get(
                `${API}/user/${userId}/bookings`
            );
            setBookings(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Booking fetch failed:", error);
            setNotice({
                type: "error",
                text: "Unable to load your reservations. Please try again.",
            });
        } finally {
            setBookingsLoading(false);
        }
    }, []);

    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem("user") || "null");

            if (!stored) return;

            setUser(stored);
            setForm({
                name: stored.name || "",
                email: stored.email || "",
                phone: stored.phone || "",
                nationality: stored.nationality || "",
            });

            if (stored.id) fetchBookings(stored.id);
        } catch (error) {
            console.error("Unable to read user:", error);
        }
    }, [fetchBookings]);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!reviewModal) return;

        const onKeyDown = (event) => {
            if (event.key === "Escape" && !reviewLoading) {
                setReviewModal(false);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [reviewModal, reviewLoading]);

    const notify = (type, text) => setNotice({ type, text });

    const updateProfile = async (event) => {
        event.preventDefault();
        if (!user?.id) return;

        setProfileLoading(true);
        setNotice(null);

        try {
            const response = await axios.put(
                `${API}/user/${user.id}`,
                form
            );

            const updated = response.data.user;

            localStorage.setItem("user", JSON.stringify(updated));
            setUser(updated);
            setForm({
                name: updated.name || "",
                email: updated.email || "",
                phone: updated.phone || "",
                nationality: updated.nationality || "",
            });

            notify("success", "Your profile has been updated successfully.");
        } catch (error) {
            notify(
                "error",
                error.response?.data?.message || "Unable to update your profile."
            );
        } finally {
            setProfileLoading(false);
        }
    };

    const cancelBooking = async (booking) => {
        if (!window.confirm("Are you sure you want to cancel this booking?")) {
            return;
        }

        setCancelLoading(booking.id);
        setNotice(null);

        try {
            await axios.post(`${API}/booking/${booking.id}/cancel`);
            notify("success", "Your booking has been cancelled.");
            await fetchBookings(user.id);
        } catch (error) {
            notify(
                "error",
                error.response?.data?.message || "Unable to cancel booking."
            );
        } finally {
            setCancelLoading(null);
        }
    };

    const openReview = (booking) => {
        setSelectedBooking(booking);
        setRating(Number(booking.review?.rating || 0));
        setComment(booking.review?.comment || "");
        setReviewModal(true);
    };

    const closeReview = () => {
        if (reviewLoading) return;
        setReviewModal(false);
        setSelectedBooking(null);
    };

    const submitReview = async () => {
        if (!rating || !selectedBooking || !user) return;

        setReviewLoading(true);
        setNotice(null);

        try {
            if (selectedBooking.review) {
                await axios.put(
                    `${API}/reviews/${selectedBooking.review.id}`,
                    { rating, comment }
                );
            } else {
                await axios.post(`${API}/reviews`, {
                    booking_id: selectedBooking.id,
                    hotel_id: selectedBooking.hotel_id,
                    email: user.email,
                    rating,
                    comment,
                });
            }

            notify(
                "success",
                selectedBooking.review
                    ? "Your review has been updated."
                    : "Thank you! Your review has been submitted."
            );

            setReviewModal(false);
            setSelectedBooking(null);
            setRating(0);
            setComment("");
            await fetchBookings(user.id);
        } catch (error) {
            notify(
                "error",
                error.response?.data?.message || "Unable to save your review."
            );
        } finally {
            setReviewLoading(false);
        }
    };

    const deleteReview = async (reviewId) => {
        if (!window.confirm("Are you sure you want to delete this review?")) {
            return;
        }

        setDeleteLoading(reviewId);
        setNotice(null);

        try {
            await axios.delete(`${API}/reviews/${reviewId}`);
            notify("success", "Your review has been deleted.");
            await fetchBookings(user.id);
        } catch (error) {
            notify(
                "error",
                error.response?.data?.message || "Unable to delete review."
            );
        } finally {
            setDeleteLoading(null);
        }
    };

    const confirmedCount = bookings.filter(
        (booking) => String(booking.status).toLowerCase() === "confirmed"
    ).length;

    const reviewedCount = bookings.filter(
        (booking) => Boolean(booking.review)
    ).length;

    const initials = (user?.name || "G")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();

    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F5FAF7] px-4 py-28">
                <div className="w-full max-w-[490px] overflow-hidden rounded-[26px] border border-[#DCEAE1] bg-white shadow-[0_25px_65px_rgba(6,78,59,0.08)]">
                    <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />
                    <div className="p-8 text-center sm:p-12">
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] bg-[#E8F7EE] text-[#168B43]">
                            <LockKeyhole size={35} />
                        </div>
                        <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                            My Account
                        </p>
                        <h1 className="mt-3 text-[35px] font-extrabold tracking-tight text-[#17324D]">
                            Welcome <span className="text-[#168B43]">Back.</span>
                        </h1>
                        <p className="mt-4 text-sm leading-8 text-[#718096]">
                            Sign in to manage your personal details, hotel bookings,
                            cancellations and guest reviews.
                        </p>
                        <button
                            type="button"
                            onClick={() => onLoginClick?.()}
                            className={`${primaryBtn} mt-8 w-full`}
                        >
                            Sign In To Your Account <ArrowRight size={18} />
                        </button>
                        <p className="mt-6 text-xs text-[#9AA8B5]">
                            Your Dubai travel experience, all in one place.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <main className="min-h-screen overflow-hidden bg-[#F5FAF7] text-[#17324D]">
            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-[#064E3B] pt-28 text-white sm:pt-32">
                <div className="pointer-events-none absolute -right-20 -top-24 -z-10 h-[420px] w-[420px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute right-10 top-10 -z-10 h-[300px] w-[300px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28] via-[#064E3B] to-[#08794B]" />

                <div className="mx-auto max-w-[1536px] px-4 pb-14 sm:px-6 sm:pb-16 lg:px-8">
                    <nav className="mb-8 flex items-center gap-2 text-xs text-white/70">
                        <Link to="/" className="hover:text-white">Home</Link>
                        <ChevronRight size={14} />
                        <span className="text-[#B6F2CB]">My Account</span>
                    </nav>

                    <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                        <div className="max-w-[760px]">
                            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                                <Sparkles size={15} /> Guest Dashboard
                            </span>
                            <h1 className="mt-5 break-words text-[40px] font-extrabold leading-tight tracking-tight sm:text-[55px]">
                                Welcome Back,
                                <br />
                                <span className="text-[#A5E8B8]">{user.name}.</span>
                            </h1>
                            <p className="mt-5 max-w-[600px] text-sm leading-8 text-white/75 sm:text-base">
                                Your personal travel space to manage reservations,
                                update your information and share your experiences.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            {[
                                { label: "Total Bookings", value: bookings.length, icon: Hotel },
                                { label: "Confirmed", value: confirmedCount, icon: CheckCircle2 },
                                { label: "Reviews", value: reviewedCount, icon: Star },
                            ].map(({ label, value, icon: Icon }) => (
                                <div
                                    key={label}
                                    className="min-w-[125px] rounded-2xl border border-white/20 bg-white/10 px-5 py-4 backdrop-blur-sm"
                                >
                                    <Icon size={19} className="text-[#A5E8B8]" />
                                    <p className="mt-3 text-[27px] font-extrabold text-white">
                                        {value}
                                    </p>
                                    <p className="mt-1 text-[11px] font-semibold text-white/65">
                                        {label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DASHBOARD */}
            <section className="py-14 sm:py-20">
                <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="mb-9">
                        <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#168B43]">
                            Your Personal Space
                        </p>
                        <h2 className="mt-3 text-[32px] font-extrabold tracking-tight sm:text-[43px]">
                            Account <span className="text-[#168B43]">Dashboard.</span>
                        </h2>
                        <p className="mt-3 text-sm leading-7 text-[#718096]">
                            Everything you need to manage your Dubai hotel stays.
                        </p>
                    </div>

                    {notice && (
                        <div
                            role={notice.type === "error" ? "alert" : "status"}
                            className={`mb-7 flex items-start gap-3 rounded-xl border p-4 text-sm ${notice.type === "error"
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-emerald-200 bg-emerald-50 text-emerald-800"
                                }`}
                        >
                            {notice.type === "error" ? (
                                <CircleAlert size={20} className="shrink-0" />
                            ) : (
                                <CheckCircle2 size={20} className="shrink-0" />
                            )}
                            <span className="flex-1">{notice.text}</span>
                            <button
                                type="button"
                                onClick={() => setNotice(null)}
                                aria-label="Dismiss notification"
                            >
                                <X size={17} />
                            </button>
                        </div>
                    )}

                    <div className="grid items-start gap-8 lg:grid-cols-[285px_minmax(0,1fr)]">
                        {/* SIDEBAR */}
                        <aside className="lg:sticky lg:top-24">
                            <div className="overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-sm">
                                <div className="bg-gradient-to-br from-[#064E3B] to-[#168B43] p-6 text-center">
                                    <div className="mx-auto flex h-[76px] w-[76px] items-center justify-center rounded-[22px] border-4 border-white/25 bg-white text-[25px] font-black text-[#168B43] shadow-lg">
                                        {initials}
                                    </div>
                                    <h3 className="mt-4 break-words text-lg font-extrabold text-white">
                                        {user.name}
                                    </h3>
                                    <p className="mt-1 break-all text-xs text-white/70">
                                        {user.email}
                                    </p>
                                    <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-bold text-white">
                                        <CheckCircle2 size={13} /> Active Account
                                    </span>
                                </div>

                                <div className="p-4">
                                    <p className="px-3 pb-3 pt-2 text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#9AA8B5]">
                                        Account Navigation
                                    </p>

                                    {[
                                        { id: "profile", title: "My Profile", icon: UserRound },
                                        { id: "bookings", title: "My Bookings", icon: Hotel },
                                    ].map(({ id, title, icon: Icon }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => setActiveTab(id)}
                                            className={`mb-2 flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-sm font-extrabold transition ${activeTab === id
                                                ? "bg-[#168B43] text-white"
                                                : "text-[#516779] hover:bg-[#E8F7EE] hover:text-[#168B43]"
                                                }`}
                                        >
                                            <Icon size={19} />
                                            <span className="flex-1">{title}</span>
                                            {id === "bookings" ? (
                                                <span className={`rounded-full px-2.5 py-1 text-xs ${activeTab === id
                                                    ? "bg-white/20"
                                                    : "bg-[#E8F7EE] text-[#168B43]"
                                                    }`}>
                                                    {bookings.length}
                                                </span>
                                            ) : (
                                                <ChevronRight size={16} />
                                            )}
                                        </button>
                                    ))}

                                    <Link
                                        to="/wishlist"
                                        className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-extrabold text-[#516779] transition hover:bg-[#E8F7EE] hover:text-[#168B43]"
                                    >
                                        <Heart size={19} />
                                        <span className="flex-1">My Wishlist</span>
                                        <ArrowRight size={16} />
                                    </Link>

                                    <div className="mt-5 rounded-xl bg-[#F0F8F3] p-4">
                                        <ShieldCheck size={23} className="text-[#168B43]" />
                                        <p className="mt-3 text-sm font-extrabold">
                                            Your Account
                                        </p>
                                        <p className="mt-2 text-xs leading-6 text-[#718096]">
                                            Keep your contact details updated for a smoother
                                            booking experience.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </aside>

                        {/* PROFILE */}
                        <div className="min-w-0">
                            {activeTab === "profile" && (
                                <div className="overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-sm">
                                    <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />
                                    <div className="p-6 sm:p-9 lg:p-10">
                                        <div className="mb-9 flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-[#E8F7EE] p-3 text-[#168B43]">
                                                    <UserRound size={25} />
                                                </div>
                                                <h2 className="mt-5 text-[28px] font-extrabold tracking-tight sm:text-[34px]">
                                                    Personal <span className="text-[#168B43]">Information.</span>
                                                </h2>
                                                <p className="mt-2 text-sm leading-7 text-[#718096]">
                                                    Manage your details and keep your profile up to date.
                                                </p>
                                            </div>
                                            <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F7EE] px-4 py-2 text-[11px] font-extrabold text-[#168B43]">
                                                <CheckCircle2 size={15} /> Account Active
                                            </span>
                                        </div>

                                        <form onSubmit={updateProfile} className="grid gap-6 sm:grid-cols-2">
                                            <InputField
                                                label="Full Name"
                                                name="name"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                icon={UserRound}
                                                placeholder="Enter your full name"
                                            />
                                            <InputField
                                                label="Email Address"
                                                name="email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                icon={Mail}
                                                placeholder="Enter your email"
                                            />
                                            <InputField
                                                label="Phone Number"
                                                name="phone"
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                                icon={Phone}
                                                placeholder="Enter your phone number"
                                            />
                                            <InputField
                                                label="Nationality"
                                                name="nationality"
                                                value={form.nationality}
                                                onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                                                icon={Globe2}
                                                placeholder="Enter your nationality"
                                            />

                                            <div className="flex flex-wrap items-center justify-between gap-5 border-t border-[#E3ECE6] pt-7 sm:col-span-2">
                                                <p className="flex items-center gap-2 text-xs text-[#718096]">
                                                    <ShieldCheck size={18} className="text-[#168B43]" />
                                                    Your profile information is saved securely.
                                                </p>
                                                <button
                                                    type="submit"
                                                    disabled={profileLoading}
                                                    className={primaryBtn}
                                                >
                                                    {profileLoading ? (
                                                        "Updating..."
                                                    ) : (
                                                        <>
                                                            <Save size={18} /> Save Changes
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* BOOKINGS */}
                            {activeTab === "bookings" && (
                                <div className="overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-sm">
                                    <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />
                                    <div className="p-5 sm:p-8 lg:p-9">
                                        <div className="mb-8 flex flex-wrap items-center justify-between gap-5">
                                            <div>
                                                <p className="text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                                                    Your Reservations
                                                </p>
                                                <h2 className="mt-3 text-[29px] font-extrabold tracking-tight sm:text-[36px]">
                                                    My <span className="text-[#168B43]">Bookings.</span>
                                                </h2>
                                                <p className="mt-2 text-sm text-[#718096]">
                                                    Manage your hotel stays, cancellations and reviews.
                                                </p>
                                            </div>
                                            <div className="rounded-2xl bg-[#E8F7EE] px-5 py-4 text-center">
                                                <p className="text-[26px] font-extrabold text-[#168B43]">
                                                    {bookings.length}
                                                </p>
                                                <p className="text-[11px] font-bold text-[#718096]">
                                                    Reservations
                                                </p>
                                            </div>
                                        </div>

                                        {bookingsLoading && bookings.length === 0 ? (
                                            <div className="py-16 text-center text-sm text-[#718096]">
                                                Loading your reservations...
                                            </div>
                                        ) : bookings.length === 0 ? (
                                            <div className="rounded-[20px] border border-dashed border-[#CDE3D5] bg-[#F8FBF9] px-6 py-16 text-center">
                                                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] bg-[#E8F7EE] text-[#168B43]">
                                                    <Hotel size={35} />
                                                </div>
                                                <h3 className="mt-6 text-2xl font-extrabold">
                                                    No Bookings Yet
                                                </h3>
                                                <p className="mx-auto mt-3 max-w-[430px] text-sm leading-7 text-[#718096]">
                                                    Your hotel reservations will appear here after
                                                    you complete your first booking.
                                                </p>
                                                <Link to="/hotels" className={`${primaryBtn} mt-7`}>
                                                    Explore Dubai Hotels <ArrowRight size={17} />
                                                </Link>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {bookings.map((booking) => {
                                                    const status = String(booking.status || "").toLowerCase();
                                                    const deadline = getDeadline(booking);
                                                    const canCancel =
                                                        status === "confirmed" && deadline > now;

                                                    const checkout = new Date(booking.check_out);
                                                    const today = new Date(now);
                                                    today.setHours(0, 0, 0, 0);
                                                    checkout.setHours(0, 0, 0, 0);

                                                    const completed =
                                                        !Number.isNaN(checkout.getTime()) &&
                                                        checkout < today;

                                                    return (
                                                        <article
                                                            key={booking.id}
                                                            className="overflow-hidden rounded-[20px] border border-[#DCEAE1] bg-white transition hover:border-[#B5DCC1] hover:shadow-[0_15px_35px_rgba(6,78,59,0.07)]"
                                                        >
                                                            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#E3ECE6] bg-[#F8FBF9] p-5 sm:p-6">
                                                                <div className="flex min-w-0 items-start gap-4">
                                                                    <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] p-3 text-[#168B43]">
                                                                        <Hotel size={25} />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                                                                            Reservation #{booking.id}
                                                                        </p>
                                                                        <h3 className="mt-2 break-words text-xl font-extrabold text-[#17324D]">
                                                                            {booking.hotel_name || "Hotel Booking"}
                                                                        </h3>
                                                                        <p className="mt-2 text-xs leading-6 text-[#718096]">
                                                                            {booking.room_name || "Room"} · Room{" "}
                                                                            {booking.room_number || "—"}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <Status status={booking.status} />
                                                            </div>

                                                            <div className="p-5 sm:p-6">
                                                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                                    <DetailTile icon={CalendarDays} label="Check-in">
                                                                        {formatDate(booking.check_in)}
                                                                    </DetailTile>
                                                                    <DetailTile icon={CalendarDays} label="Check-out">
                                                                        {formatDate(booking.check_out)}
                                                                    </DetailTile>
                                                                    <DetailTile icon={Users} label="Guests">
                                                                        {booking.guests ?? "—"}{" "}
                                                                        {Number(booking.guests) === 1 ? "Guest" : "Guests"}
                                                                    </DetailTile>
                                                                    <DetailTile label="Amount Paid" accent>
                                                                        AED {formatMoney(booking.total_amount)}
                                                                    </DetailTile>
                                                                </div>

                                                                {status === "confirmed" && (
                                                                    <div className="mt-5 flex flex-wrap items-center gap-3">
                                                                        {canCancel && (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => cancelBooking(booking)}
                                                                                    disabled={cancelLoading === booking.id}
                                                                                    className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-xs font-extrabold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                                                                                >
                                                                                    <XCircle size={16} />
                                                                                    {cancelLoading === booking.id
                                                                                        ? "Cancelling..."
                                                                                        : "Cancel Booking"}
                                                                                </button>

                                                                                <span className="inline-flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-xs font-bold text-amber-800">
                                                                                    <Clock3 size={16} />
                                                                                    Cancellation countdown:{" "}
                                                                                    <span className="tabular-nums">
                                                                                        {countdown(deadline, now)}
                                                                                    </span>
                                                                                </span>
                                                                            </>
                                                                        )}

                                                                        {completed && !booking.review && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openReview(booking)}
                                                                                className={primaryBtn}
                                                                            >
                                                                                <Star size={17} /> Leave a Review
                                                                            </button>
                                                                        )}

                                                                        {!completed && (
                                                                            <p className="flex items-center gap-2 text-xs text-[#718096]">
                                                                                <Clock3 size={15} />
                                                                                Reviews are available after checkout.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {booking.review && (
                                                                    <div className="mt-6 rounded-2xl border border-[#DCEAE1] bg-[#F8FBF9] p-5">
                                                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                                                            <div>
                                                                                <h4 className="text-sm font-extrabold">
                                                                                    Your Hotel Review
                                                                                </h4>
                                                                                <div className="mt-2">
                                                                                    <RatingStars rating={booking.review.rating} />
                                                                                </div>
                                                                            </div>
                                                                            <span className="rounded-full bg-amber-50 px-3 py-2 text-xs font-extrabold text-amber-700">
                                                                                {booking.review.rating}/5 Rating
                                                                            </span>
                                                                        </div>

                                                                        <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-[#718096]">
                                                                            {booking.review.comment || "No written comment."}
                                                                        </p>

                                                                        <div className="mt-5 flex flex-wrap gap-3">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openReview(booking)}
                                                                                className="inline-flex items-center gap-2 rounded-xl bg-[#17324D] px-4 py-2.5 text-xs font-extrabold text-white transition hover:bg-[#064E3B]"
                                                                            >
                                                                                <Edit3 size={15} /> Edit Review
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => deleteReview(booking.review.id)}
                                                                                disabled={deleteLoading === booking.review.id}
                                                                                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-extrabold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                                                                            >
                                                                                <Trash2 size={15} />
                                                                                {deleteLoading === booking.review.id
                                                                                    ? "Deleting..."
                                                                                    : "Delete Review"}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* REVIEW MODAL */}
            {reviewModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[#032F28]/80 px-4 py-6 backdrop-blur-sm"
                    onClick={closeReview}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="review-modal-title"
                        onClick={(event) => event.stopPropagation()}
                        className="my-auto w-full max-w-[500px] overflow-hidden rounded-[24px] bg-white shadow-2xl"
                    >
                        <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />
                        <div className="p-6 sm:p-9">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-[#E8F7EE] p-3 text-[#168B43]">
                                        <Star size={24} />
                                    </div>
                                    <p className="mt-5 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                                        Guest Experience
                                    </p>
                                    <h2
                                        id="review-modal-title"
                                        className="mt-2 text-[29px] font-extrabold text-[#17324D]"
                                    >
                                        {selectedBooking?.review ? "Edit Your Review" : "Share Your Review"}
                                    </h2>
                                    <p className="mt-2 text-sm text-[#718096]">
                                        {selectedBooking?.hotel_name}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeReview}
                                    disabled={reviewLoading}
                                    aria-label="Close review"
                                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F0F5F2] text-[#718096] hover:bg-[#E8F7EE]"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="mt-8">
                                <p className="mb-4 text-sm font-extrabold text-[#17324D]">
                                    How was your stay?
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {[1, 2, 3, 4, 5].map((number) => (
                                        <button
                                            key={number}
                                            type="button"
                                            disabled={reviewLoading}
                                            onClick={() => setRating(number)}
                                            aria-label={`Rate ${number} stars`}
                                            aria-pressed={rating === number}
                                            className={`flex h-12 w-12 items-center justify-center rounded-xl transition ${number <= rating
                                                ? "bg-amber-100 text-amber-500"
                                                : "bg-[#F0F5F2] text-[#B7C6BD]"
                                                }`}
                                        >
                                            <Star
                                                size={23}
                                                className={number <= rating ? "fill-current" : ""}
                                            />
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-3 text-xs text-[#718096]">
                                    {["Select your rating", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating]}
                                </p>
                            </div>

                            <div className="mt-7">
                                <label
                                    htmlFor="review-comment"
                                    className="mb-2 block text-sm font-extrabold"
                                >
                                    Your Experience
                                </label>
                                <textarea
                                    id="review-comment"
                                    rows={5}
                                    value={comment}
                                    onChange={(event) => setComment(event.target.value)}
                                    disabled={reviewLoading}
                                    placeholder="Tell us about your hotel experience..."
                                    className="w-full resize-y rounded-xl border border-[#DCEAE1] bg-[#F8FBF9] p-4 text-sm leading-7 outline-none focus:border-[#168B43] focus:ring-4 focus:ring-[#168B43]/10"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={submitReview}
                                disabled={reviewLoading || rating === 0}
                                className={`${primaryBtn} mt-6 w-full`}
                            >
                                {reviewLoading
                                    ? "Saving..."
                                    : selectedBooking?.review
                                        ? "Update Review"
                                        : "Submit Review"}
                                {!reviewLoading && <ArrowRight size={17} />}
                            </button>
                            <button
                                type="button"
                                onClick={closeReview}
                                disabled={reviewLoading}
                                className="mt-3 w-full rounded-xl border border-[#DCEAE1] px-5 py-3 text-sm font-bold text-[#718096] transition hover:bg-[#F8FBF9]"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
